import { useEffect, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Check, Palette, Sun, Moon } from 'lucide-react'
import { useUIStore } from '../../stores/uiStore'

// ─── Presets ─────────────────────────────────────────────────────────────────

export const ACCENT_PRESETS = [
  { id: 'ember',   label: 'Ember',   color: '#E07040', hover: '#F07848', active: '#C86030', darkSubtle: '#1E1108', lightSubtle: '#FFF0E6', focus: '#C87941' },
  { id: 'ocean',   label: 'Ocean',   color: '#4F8EF7', hover: '#6BA0FF', active: '#3A78E0', darkSubtle: '#071220', lightSubtle: '#EFF6FF', focus: '#4F8EF7' },
  { id: 'violet',  label: 'Violet',  color: '#8B5CF6', hover: '#9D70FF', active: '#7C3AED', darkSubtle: '#100D20', lightSubtle: '#F5F3FF', focus: '#8B5CF6' },
  { id: 'rose',    label: 'Rose',    color: '#EC4899', hover: '#F472B6', active: '#DB2777', darkSubtle: '#200A14', lightSubtle: '#FDF2F8', focus: '#EC4899' },
  { id: 'teal',    label: 'Teal',    color: '#14B8A6', hover: '#2DD4BF', active: '#0D9488', darkSubtle: '#071412', lightSubtle: '#F0FDFA', focus: '#14B8A6' },
  { id: 'sage',    label: 'Sage',    color: '#6DB87A', hover: '#86CC92', active: '#4D9E5A', darkSubtle: '#0A1A0C', lightSubtle: '#F0FDF4', focus: '#6DB87A' },
  { id: 'crimson', label: 'Crimson', color: '#E85454', hover: '#F06868', active: '#CC3535', darkSubtle: '#1A0808', lightSubtle: '#FEF2F2', focus: '#E85454' },
  { id: 'gold',    label: 'Gold',    color: '#F59E0B', hover: '#FBBF24', active: '#D97706', darkSubtle: '#1A1204', lightSubtle: '#FFFBEB', focus: '#F59E0B' },
  { id: 'indigo',  label: 'Indigo',  color: '#6366F1', hover: '#818CF8', active: '#4F46E5', darkSubtle: '#0D0E20', lightSubtle: '#EEF2FF', focus: '#6366F1' },
  { id: 'cyan',    label: 'Cyan',    color: '#06B6D4', hover: '#22D3EE', active: '#0891B2', darkSubtle: '#071218', lightSubtle: '#ECFEFF', focus: '#06B6D4' },
]

export const BASE_PALETTES = [
  {
    id: 'tungsten', label: 'Tungsten', description: 'Warm charcoal',
    preview: ['#0A0907', '#111009', '#161410', '#1D1A14'],
    vars: {} as Record<string, string>
  },
  {
    id: 'slate', label: 'Slate', description: 'Cool gray',
    preview: ['#0C0D0F', '#111318', '#15171C', '#1C1F26'],
    vars: {
      '--bg-root': '#0C0D0F', '--bg-sidebar': '#111318', '--bg-panel': '#15171C',
      '--bg-surface': '#1C1F26', '--bg-elevated': '#22262F', '--bg-input': '#1A1D24',
      '--bg-hover': '#1E2128', '--bg-selected': '#1D2535', '--bg-selected-strong': '#253040',
      '--border': '#252930', '--border-strong': '#323742',
      '--text-primary': '#E8EAF0', '--text-secondary': '#8A92A8',
      '--text-tertiary': '#52596A', '--text-disabled': '#363C4A',
    }
  },
  {
    id: 'midnight', label: 'Midnight', description: 'Deep navy',
    preview: ['#060810', '#080B16', '#0C1020', '#101524'],
    vars: {
      '--bg-root': '#060810', '--bg-sidebar': '#080B16', '--bg-panel': '#0C1020',
      '--bg-surface': '#101524', '--bg-elevated': '#151C2E', '--bg-input': '#0E1220',
      '--bg-hover': '#141A2C', '--bg-selected': '#162240', '--bg-selected-strong': '#1C2E55',
      '--border': '#1A2238', '--border-strong': '#243050',
      '--text-primary': '#DDE3F8', '--text-secondary': '#7A8AAE',
      '--text-tertiary': '#485068', '--text-disabled': '#2E3850',
    }
  },
  {
    id: 'forest', label: 'Forest', description: 'Dark green',
    preview: ['#080C08', '#0C1209', '#10160E', '#151D13'],
    vars: {
      '--bg-root': '#080C08', '--bg-sidebar': '#0C1209', '--bg-panel': '#10160E',
      '--bg-surface': '#151D13', '--bg-elevated': '#1C251A', '--bg-input': '#121810',
      '--bg-hover': '#182015', '--bg-selected': '#1A2C16', '--bg-selected-strong': '#223A1C',
      '--border': '#1E2C1B', '--border-strong': '#2A3E26',
      '--text-primary': '#DFF0DC', '--text-secondary': '#7A9E74',
      '--text-tertiary': '#4A6645', '--text-disabled': '#304530',
    }
  },
  {
    id: 'obsidian', label: 'Obsidian', description: 'Pure black',
    preview: ['#000000', '#0A0A0A', '#0F0F0F', '#141414'],
    vars: {
      '--bg-root': '#000000', '--bg-sidebar': '#0A0A0A', '--bg-panel': '#0F0F0F',
      '--bg-surface': '#141414', '--bg-elevated': '#1A1A1A', '--bg-input': '#111111',
      '--bg-hover': '#1C1C1C', '--bg-selected': '#1F1F1F', '--bg-selected-strong': '#252525',
      '--border': '#222222', '--border-strong': '#2E2E2E',
      '--text-primary': '#F0F0F0', '--text-secondary': '#888888',
      '--text-tertiary': '#555555', '--text-disabled': '#333333',
    }
  },
]

// All palette CSS var names (used to clear before applying a new palette)
const ALL_PALETTE_VARS = [
  '--bg-root', '--bg-sidebar', '--bg-panel', '--bg-surface', '--bg-elevated',
  '--bg-input', '--bg-hover', '--bg-selected', '--bg-selected-strong',
  '--border', '--border-strong',
  '--text-primary', '--text-secondary', '--text-tertiary', '--text-disabled',
]

// ─── Theme Application ────────────────────────────────────────────────────────

function applyAccent(accent: typeof ACCENT_PRESETS[0], isLight: boolean) {
  const root = document.documentElement
  root.style.setProperty('--brand', accent.color)
  root.style.setProperty('--brand-hover', accent.hover)
  root.style.setProperty('--brand-active', accent.active)
  root.style.setProperty('--brand-subtle', isLight ? accent.lightSubtle : accent.darkSubtle)
  root.style.setProperty('--border-focus', accent.focus)
}

function applyPalette(palette: typeof BASE_PALETTES[0]) {
  const root = document.documentElement
  // Remove all palette inline styles first so CSS selector rules can apply
  for (const varName of ALL_PALETTE_VARS) {
    root.style.removeProperty(varName)
  }
  // Apply new palette vars (empty vars = use CSS selector rules, i.e. default tungsten)
  for (const [key, value] of Object.entries(palette.vars)) {
    root.style.setProperty(key, value)
  }
}

export function applyStoredTheme() {
  try {
    const accentId = localStorage.getItem('tmail-accent') ?? 'ember'
    const paletteId = localStorage.getItem('tmail-palette')
    const isLight = localStorage.getItem('tmail_theme') === 'light'

    // Always apply accent so --brand-subtle is correct for the current mode
    const accent = ACCENT_PRESETS.find((a) => a.id === accentId) ?? ACCENT_PRESETS[0]
    applyAccent(accent, isLight)

    if (isLight) {
      // Palettes are dark-mode only — clear any active palette inline styles
      const root = document.documentElement
      for (const varName of ALL_PALETTE_VARS) root.style.removeProperty(varName)
    } else if (paletteId) {
      const palette = BASE_PALETTES.find((p) => p.id === paletteId)
      if (palette) applyPalette(palette)
    }
  } catch {
    // ignore localStorage errors
  }
}

// ─── ThemeCustomizer Component ────────────────────────────────────────────────

interface ThemeCustomizerProps {
  open: boolean
  onClose: () => void
}

export function ThemeCustomizer({ open, onClose }: ThemeCustomizerProps) {
  const { theme, toggleTheme, density, setDensity } = useUIStore()
  const isLight = theme === 'light'

  // Track active selections in state so checkmarks update immediately on click
  const [activeAccentId, setActiveAccentId] = useState<string>(() => {
    try { return localStorage.getItem('tmail-accent') ?? 'ocean' } catch { return 'ocean' }
  })
  const [activePaletteId, setActivePaletteId] = useState<string>(() => {
    try { return localStorage.getItem('tmail-palette') ?? 'tungsten' } catch { return 'tungsten' }
  })

  // Apply stored theme on mount
  useEffect(() => {
    applyStoredTheme()
  }, [])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  const handleAccentSelect = useCallback((accent: typeof ACCENT_PRESETS[0]) => {
    try { localStorage.setItem('tmail-accent', accent.id) } catch { /* ignore */ }
    applyAccent(accent, isLight)
    setActiveAccentId(accent.id)
  }, [isLight])

  const handlePaletteSelect = useCallback((palette: typeof BASE_PALETTES[0]) => {
    try { localStorage.setItem('tmail-palette', palette.id) } catch { /* ignore */ }
    applyPalette(palette)
    setActivePaletteId(palette.id)
    window.dispatchEvent(new CustomEvent('tmail:palette-change', { detail: palette.id }))
  }, [])

  const handleLightToggle = () => {
    if (theme === 'dark') toggleTheme()
  }

  const handleDarkToggle = () => {
    if (theme === 'light') toggleTheme()
  }

  const content = (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.45)',
              zIndex: 1400,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            key="drawer"
            className="tc-drawer"
            initial={{ x: 320 }}
            animate={{ x: 0 }}
            exit={{ x: 320 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Header */}
            <div className="tc-header">
              <div className="tc-header__title">
                <Palette size={16} style={{ color: 'var(--brand)', flexShrink: 0 }} />
                <span>Theme Studio</span>
              </div>
              <button className="tc-close" onClick={onClose} aria-label="Close">
                <X size={16} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="tc-body">

              {/* ── Accent Color ── */}
              <div className="tc-section">
                <div className="tc-section__label">ACCENT COLOR</div>
                <div className="tc-accents">
                  {ACCENT_PRESETS.map((accent) => {
                    const isActive = activeAccentId === accent.id
                    return (
                      <button
                        key={accent.id}
                        className={`tc-swatch${isActive ? ' tc-swatch--active' : ''}`}
                        style={{ '--swatch-color': accent.color } as React.CSSProperties}
                        onClick={() => handleAccentSelect(accent)}
                        title={accent.label}
                        aria-label={`${accent.label} accent color${isActive ? ' (selected)' : ''}`}
                      >
                        {isActive && <Check size={14} style={{ color: 'white', flexShrink: 0 }} />}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* ── Base Palette (dark only) ── */}
              {!isLight && (
                <div className="tc-section">
                  <div className="tc-section__label">
                    BASE PALETTE
                    <span className="tc-section__note">dark mode only</span>
                  </div>
                  <div className="tc-palettes">
                    {BASE_PALETTES.map((palette) => {
                      const isActive = activePaletteId === palette.id
                      return (
                        <button
                          key={palette.id}
                          className={`tc-palette-card${isActive ? ' tc-palette-card--active' : ''}`}
                          onClick={() => handlePaletteSelect(palette)}
                          aria-label={`${palette.label} palette${isActive ? ' (selected)' : ''}`}
                        >
                          <div className="tc-palette-card__preview">
                            {palette.preview.map((color, i) => (
                              <div
                                key={i}
                                className="tc-palette-card__dot"
                                style={{ background: color }}
                              />
                            ))}
                          </div>
                          <div className="tc-palette-card__name">{palette.label}</div>
                          <div className="tc-palette-card__desc">{palette.description}</div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* ── Brightness ── */}
              <div className="tc-section">
                <div className="tc-section__label">BRIGHTNESS</div>
                <div className="tc-pills">
                  <button
                    className={`tc-pill${isLight ? ' tc-pill--active' : ''}`}
                    onClick={handleLightToggle}
                    aria-pressed={isLight}
                  >
                    <Sun size={13} />
                    <span>Light</span>
                  </button>
                  <button
                    className={`tc-pill${!isLight ? ' tc-pill--active' : ''}`}
                    onClick={handleDarkToggle}
                    aria-pressed={!isLight}
                  >
                    <Moon size={13} />
                    <span>Dark</span>
                  </button>
                </div>
              </div>

              {/* ── Density ── */}
              <div className="tc-section">
                <div className="tc-section__label">DENSITY</div>
                <div className="tc-pills tc-pills--wrap">
                  {(['compact', 'comfortable', 'spacious'] as const).map((d) => (
                    <button
                      key={d}
                      className={`tc-pill${density === d ? ' tc-pill--active' : ''}`}
                      onClick={() => setDensity(d)}
                      aria-pressed={density === d}
                    >
                      <span style={{ textTransform: 'capitalize' }}>{d}</span>
                    </button>
                  ))}
                </div>
              </div>

            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )

  return createPortal(content, document.body)
}

// ─── Styles ───────────────────────────────────────────────────────────────────

export function ThemeCustomizerStyles() {
  return (
    <style>{`
      /* Drawer shell */
      .tc-drawer {
        position: fixed;
        right: 0;
        top: 0;
        bottom: 0;
        width: 320px;
        background: var(--bg-surface);
        border-left: 1px solid var(--border-strong);
        box-shadow: -8px 0 32px rgba(0,0,0,0.4);
        z-index: 1500;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      /* Header */
      .tc-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px;
        border-bottom: 1px solid var(--border);
        flex-shrink: 0;
      }

      .tc-header__title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 600;
        color: var(--text-primary);
      }

      .tc-close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border: none;
        border-radius: var(--radius-sm, 6px);
        background: transparent;
        color: var(--text-tertiary);
        cursor: pointer;
        transition: background 0.12s, color 0.12s;
        flex-shrink: 0;
      }
      .tc-close:hover {
        background: var(--bg-hover);
        color: var(--text-primary);
      }

      /* Scrollable body */
      .tc-body {
        flex: 1;
        overflow-y: auto;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 24px;
      }

      /* Section */
      .tc-section {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .tc-section__label {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.08em;
        color: var(--text-tertiary);
        text-transform: uppercase;
      }

      .tc-section__note {
        font-size: 10px;
        font-weight: 400;
        letter-spacing: 0;
        text-transform: none;
        color: var(--text-disabled, var(--text-tertiary));
        opacity: 0.7;
      }

      /* Accent swatches — 5x2 grid */
      .tc-accents {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 8px;
      }

      .tc-swatch {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 2px solid transparent;
        background: var(--swatch-color);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.12s, border-color 0.12s;
        flex-shrink: 0;
        padding: 0;
        justify-self: center;
      }

      .tc-swatch:hover {
        transform: scale(1.1);
        border-color: rgba(255,255,255,0.3);
      }

      .tc-swatch--active {
        border-color: rgba(255,255,255,0.8);
        box-shadow: 0 0 0 2px var(--swatch-color, var(--brand));
      }

      /* Palette cards — 2-col grid */
      .tc-palettes {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
      }

      .tc-palette-card {
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding: 10px;
        border-radius: var(--radius-md, 8px);
        border: 1px solid var(--border);
        background: var(--bg-elevated);
        cursor: pointer;
        text-align: left;
        transition: border-color 0.12s, background 0.12s;
      }

      .tc-palette-card:hover {
        background: var(--bg-hover);
        border-color: var(--border-strong);
      }

      .tc-palette-card--active {
        border-color: var(--brand);
        background: var(--bg-hover);
      }

      .tc-palette-card__preview {
        display: flex;
        gap: 3px;
      }

      .tc-palette-card__dot {
        width: 14px;
        height: 14px;
        border-radius: 3px;
        flex-shrink: 0;
      }

      .tc-palette-card__name {
        font-size: 12px;
        font-weight: 600;
        color: var(--text-primary);
        line-height: 1.2;
      }

      .tc-palette-card__desc {
        font-size: 10px;
        color: var(--text-tertiary);
        line-height: 1.2;
      }

      /* Pill toggle buttons */
      .tc-pills {
        display: flex;
        gap: 6px;
      }

      .tc-pills--wrap {
        flex-wrap: wrap;
      }

      .tc-pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 6px 14px;
        border-radius: var(--radius-full, 9999px);
        border: 1px solid var(--border);
        background: var(--bg-elevated);
        color: var(--text-secondary);
        font-family: var(--font-ui);
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.12s, border-color 0.12s, color 0.12s;
        white-space: nowrap;
      }

      .tc-pill:hover {
        background: var(--bg-hover);
        border-color: var(--border-strong);
        color: var(--text-primary);
      }

      .tc-pill--active {
        background: var(--brand-subtle, rgba(79,142,247,0.12));
        border-color: var(--brand);
        color: var(--brand);
      }

      .tc-pill--active:hover {
        background: var(--brand-subtle, rgba(79,142,247,0.18));
      }
    `}</style>
  )
}
